import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FaBook, FaUsers, FaComments, FaImages, FaUser, FaGift, FaCog, FaChartBar, FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { API } from '../config/api';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h1`
  color: #2d3436;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const LogoutButton = styled.button`
  background: #d63031;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #c0392b;
    transform: translateY(-2px);
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Tab = styled.button`
  background: ${props => props.active ? 'linear-gradient(45deg, #667eea, #764ba2)' : 'white'};
  color: ${props => props.active ? 'white' : '#636e72'};
  border: 2px solid ${props => props.active ? 'transparent' : '#e1e5e9'};
  padding: 1rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ContentArea = styled.div`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  min-height: 600px;
`;

const SectionTitle = styled.h2`
  color: #2d3436;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e1e5e9;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2d3436;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
`;

const AddBookButton = styled(motion.button)`
  background: linear-gradient(45deg, #00b894, #00cec9);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 184, 148, 0.3);
  }
`;

const BooksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const BookCard = styled(motion.div)`
  background: white;
  border: 2px solid #f1f2f6;
  border-radius: 15px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #667eea;
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const BookImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${props => props.src ? `url(${API}${props.src})` : 'linear-gradient(45deg, #ddd, #eee)'};
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 0.9rem;
`;

const BookTitle = styled.h3`
  color: #2d3436;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
  font-weight: 700;
`;

const BookGenre = styled.span`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const BookStatus = styled.span`
  background: ${props => props.published ? '#00b894' : '#fdcb6e'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

const BookActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'edit' ? '#667eea' : '#e74c3c'};
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 5px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  color: #2d3436;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 700;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;


const Select = styled.select`
  padding: 0.8rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  background: ${props => props.variant === 'primary' ? 'linear-gradient(45deg, #667eea, #764ba2)' : '#636e72'};
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    opacity: 0.9;
  }
`;

const AdminDashboard = ({ admin, token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('books');
  const [books, setBooks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingGalleryImage, setEditingGalleryImage] = useState(null);
  const [editingBonusContent, setEditingBonusContent] = useState(null);
  const [showBonusContentModal, setShowBonusContentModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: '',
    blurb: '',
    publishedDate: '',
    pages: '',
    rating: '',
    amazonLink: '',
    goodreadsLink: '',
    published: false,
    coverImage: null,
    galleryImages: []
  });
  const [testimonialFormData, setTestimonialFormData] = useState({
    reviewer: '',
    quote: '',
    rating: 5,
    bookTitle: ''
  });
  const [bonusContentFormData, setBonusContentFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Writing',
    readTime: ''
  });
  const [galleryFormData, setGalleryFormData] = useState({
    description: '',
    bookId: '',
    image: null
  });
  const [bonusContent, setBonusContent] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({
    maxFeaturedBooks: 6,
    maxGalleryImages: 8,
    showNewsletter: true,
    showTestimonials: true
  });
  const [aboutData, setAboutData] = useState({
    profileImage: '',
    bio: '',
    quote: ''
  });
  const [analyticsData, setAnalyticsData] = useState({
    totalVisits: 0,
    todayVisits: 0,
    yesterdayVisits: 0,
    weekVisits: 0,
    monthVisits: 0,
    uniqueVisitors: 0,
    pageViews: {},
    devices: {},
    browsers: {},
    dailyVisits: [],
    topReferrers: [],
    recentVisits: []
  });

  useEffect(() => {
    fetchBooks();
    fetchTestimonials();
    fetchGalleryImages();
    fetchDisplaySettings();
    fetchAboutData();
    fetchBonusContent();
    fetchSubscribers();
    if (activeTab === 'statistics') {
      fetchAnalytics();
    }
  }, [activeTab]);

  const fetchBooks = async () => {
    try {
      const response = await fetch(`${API}/api/admin/books`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBooks(data);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API}/api/admin/testimonials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fetchDisplaySettings = async () => {
    try {
      const response = await fetch(`${API}/api/admin/display-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDisplaySettings(data);
      }
    } catch (error) {
      console.error('Error fetching display settings:', error);
    }
  };

  const fetchAboutData = async () => {
    try {
      const response = await fetch(`${API}/api/admin/about`);
      if (response.ok) {
        const data = await response.json();
        setAboutData(data);
      }
    } catch (error) {
      console.error('Error fetching about data:', error);
    }
  };

  const fetchBonusContent = async () => {
    if (!token) {
      console.log('No token available for fetching bonus content');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/admin/bonus-content`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Fetch bonus content response status:', response.status);
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setBonusContent(data);
          console.log('Bonus content fetched successfully:', data);
        } else {
          console.error('Non-JSON response when fetching bonus content');
        }
      } else {
        console.error('Failed to fetch bonus content, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching bonus content:', error);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await fetch(`${API}/api/admin/gallery`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setGalleryImages(data);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    }
  };

  const handleDisplaySettingsChange = (e) => {
    const { name, value } = e.target;
    setDisplaySettings(prev => ({
      ...prev,
      [name]: parseInt(value)
    }));
  };

  const handleDisplaySettingsSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/api/admin/display-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(displaySettings)
      });
      
      if (response.ok) {
        alert('Display settings updated successfully!');
      } else {
        alert('Failed to update display settings');
      }
    } catch (error) {
      console.error('Error updating display settings:', error);
      alert('Error updating display settings');
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await fetch(`${API}/api/admin/subscribers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API}/api/admin/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const clearAnalytics = async () => {
    if (window.confirm('Are you sure you want to clear all analytics data? This action cannot be undone.')) {
      try {
        const response = await fetch(`${API}/api/admin/analytics`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          alert('Analytics data cleared successfully!');
          fetchAnalytics();
        }
      } catch (error) {
        console.error('Error clearing analytics:', error);
        alert('Failed to clear analytics data');
      }
    }
  };

  const handleAddBook = () => {
    setEditingBook(null);
    setFormData({
      title: '',
      description: '',
      genre: '',
      blurb: '',
      publishedDate: '',
      pages: '',
      rating: '',
      amazonLink: '',
      goodreadsLink: '',
      published: false,
      coverImage: null,
      galleryImages: []
    });
    setShowModal(true);
  };

  const handleEditBook = (book) => {
    setEditingBook(book);
    setFormData({
      title: book.title || '',
      description: book.description || '',
      genre: book.genre || '',
      blurb: book.blurb || '',
      publishedDate: book.publishedDate || '',
      pages: book.pages || '',
      rating: book.rating || '',
      amazonLink: book.amazonLink || '',
      goodreadsLink: book.goodreadsLink || '',
      published: book.published || false,
      coverImage: null,
      galleryImages: []
    });
    setShowModal(true);
  };

  const handleDeleteBook = async (bookId) => {
    if (window.confirm('Are you sure you want to delete this book?')) {
      try {
        const response = await fetch(`${API}/api/admin/books/${bookId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          fetchBooks();
        }
      } catch (error) {
        console.error('Error deleting book:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form data before submission:', formData);
    
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'coverImage' && formData[key]) {
        submitData.append('coverImage', formData[key]);
      } else if (key === 'galleryImages' && formData[key].length > 0) {
        formData[key].forEach(file => {
          submitData.append('galleryImages', file);
        });
      } else if (key !== 'coverImage' && key !== 'galleryImages') {
        console.log(`Adding to FormData: ${key} = ${formData[key]}`);
        submitData.append(key, formData[key]);
      }
    });

    try {
      const url = editingBook 
        ? `${API}/api/admin/books/${editingBook.id}`
        : `${API}/api/admin/books`;
      
      const response = await fetch(url, {
        method: editingBook ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });

      if (response.ok) {
        const updatedBook = await response.json();
        console.log('Book updated successfully:', updatedBook);
        setShowModal(false);
        fetchBooks();
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        alert('Failed to save book: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving book:', error);
      alert('Failed to save book: ' + error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'file' && name === 'galleryImages' ? Array.from(files) :
              type === 'file' ? files[0] : value
    }));
  };

  const handleTestimonialInputChange = (e) => {
    const { name, value } = e.target;
    setTestimonialFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        const response = await fetch(`${API}/api/admin/testimonials/${testimonialId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          fetchTestimonials();
          alert('Testimonial deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting testimonial:', error);
      }
    }
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingTestimonial 
        ? `${API}/api/admin/testimonials/${editingTestimonial.id}`
        : `${API}/api/admin/testimonials`;
      
      const response = await fetch(url, {
        method: editingTestimonial ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testimonialFormData)
      });

      if (response.ok) {
        setShowTestimonialModal(false);
        fetchTestimonials();
      }
    } catch (error) {
      console.error('Error saving testimonial:', error);
    }
  };

  const handleGalleryFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setGalleryFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setGalleryFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleGallerySubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('description', galleryFormData.description);
    formData.append('bookId', galleryFormData.bookId);
    if (galleryFormData.image) {
      formData.append('image', galleryFormData.image);
    }

    try {
      const url = editingGalleryImage 
        ? `${API}/api/admin/gallery/${editingGalleryImage.id}`
        : `${API}/api/admin/gallery`;
      const method = editingGalleryImage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        fetchGalleryImages();
        setShowGalleryModal(false);
        alert(editingGalleryImage ? 'Gallery image updated successfully!' : 'Gallery image added successfully!');
      }
    } catch (error) {
      console.error('Error saving gallery image:', error);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this gallery image?')) {
      try {
        const response = await fetch(`${API}/api/admin/gallery/${imageId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          alert('Gallery image deleted successfully!');
          fetchGalleryImages();
        } else {
          alert('Failed to delete gallery image');
        }
      } catch (error) {
        console.error('Error deleting gallery image:', error);
        alert('Error deleting gallery image');
      }
    }
  };

  const handleAddBonusContent = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!bonusContentFormData.title || !bonusContentFormData.content) {
      alert('Title and content are required');
      return;
    }
    
    // Validate token exists
    if (!token) {
      alert('Authentication token is missing. Please log in again.');
      return;
    }
    
    try {
      console.log('Submitting bonus content:', bonusContentFormData);
      console.log('Using token:', token ? 'Token exists' : 'No token');
      
      const response = await fetch(`${API}/api/admin/bonus-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bonusContentFormData)
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response received:', textResponse);
        alert('Server returned an unexpected response format. Check console for details.');
        return;
      }
      
      if (response.ok) {
        const result = await response.json();
        console.log('Success response:', result);
        alert('Bonus content added successfully!');
        setBonusContentFormData({
          title: '',
          content: '',
          excerpt: '',
          category: 'Writing',
          readTime: ''
        });
        fetchBonusContent();
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        alert(`Failed to add bonus content: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Network or parsing error:', error);
      alert('Error adding bonus content: ' + error.message);
    }
  };

  const handleEditBonusContent = (content) => {
    setEditingBonusContent(content);
    setBonusContentFormData({
      title: content.title,
      content: content.content,
      excerpt: content.excerpt || '',
      category: content.category,
      readTime: content.readTime || ''
    });
    setShowBonusContentModal(true);
  };

  const handleUpdateBonusContent = async (e) => {
    e.preventDefault();
    
    if (!bonusContentFormData.title || !bonusContentFormData.content) {
      alert('Title and content are required');
      return;
    }
    
    if (!token) {
      alert('Authentication token is missing. Please log in again.');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/admin/bonus-content/${editingBonusContent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bonusContentFormData)
      });
      
      if (response.ok) {
        alert('Bonus content updated successfully!');
        setShowBonusContentModal(false);
        setEditingBonusContent(null);
        setBonusContentFormData({
          title: '',
          content: '',
          excerpt: '',
          category: 'Writing',
          readTime: ''
        });
        fetchBonusContent();
      } else {
        const errorData = await response.json();
        alert(`Failed to update bonus content: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating bonus content:', error);
      alert('Error updating bonus content: ' + error.message);
    }
  };

  const handleDeleteBonusContent = async (id) => {
    if (window.confirm('Are you sure you want to delete this bonus content?')) {
      try {
        const response = await fetch(`${API}/api/admin/bonus-content/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          alert('Bonus content deleted successfully!');
          fetchBonusContent();
        } else {
          alert('Failed to delete bonus content');
        }
      } catch (error) {
        console.error('Error deleting bonus content:', error);
        alert('Error deleting bonus content');
      }
    }
  };

  const handleAboutImageUpload = async (file) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('profileImage', file);
      
      const response = await fetch(`${API}/api/admin/about`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        alert('Profile image uploaded successfully!');
        fetchAboutData();
      } else {
        alert('Failed to upload profile image');
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      alert('Error uploading profile image');
    }
  };

  const handleUpdateAboutData = async () => {
    try {
      const formData = new FormData();
      formData.append('bio', aboutData.bio);
      formData.append('quote', aboutData.quote);
      
      const response = await fetch(`${API}/api/admin/about`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        alert('About data updated successfully!');
        fetchAboutData();
      } else {
        alert('Failed to update about data');
      }
    } catch (error) {
      console.error('Error updating about data:', error);
      alert('Error updating about data');
    }
  };

  return (
    <DashboardContainer>
      <Header>
        <Title>Admin Dashboard</Title>
        <LogoutButton onClick={onLogout}>Logout</LogoutButton>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'books'} onClick={() => setActiveTab('books')}>
          Books
        </Tab>
        <Tab active={activeTab === 'portfolio'} onClick={() => setActiveTab('portfolio')}>
          Portfolio
        </Tab>
        <Tab active={activeTab === 'testimonials'} onClick={() => setActiveTab('testimonials')}>
          Testimonials
        </Tab>
        <Tab active={activeTab === 'gallery'} onClick={() => setActiveTab('gallery')}>
          Gallery
        </Tab>
        <Tab active={activeTab === 'subscribers'} onClick={() => setActiveTab('subscribers')}>
          Subscribers
        </Tab>
        <Tab active={activeTab === 'about'} onClick={() => setActiveTab('about')}>
          About
        </Tab>
        <Tab active={activeTab === 'bonus'} onClick={() => setActiveTab('bonus')}>
          Bonus Content
        </Tab>
        <Tab active={activeTab === 'display'} onClick={() => setActiveTab('display')}>
          Display Settings
        </Tab>
        <Tab 
          active={activeTab === 'statistics'} 
          onClick={() => setActiveTab('statistics')}
        >
          Analytics
        </Tab>
      </TabContainer>

      <ContentArea>
        {activeTab === 'books' && (
          <>
            <AddBookButton
              onClick={handleAddBook}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              + Add New Book
            </AddBookButton>

            <BooksGrid>
              {books.map(book => (
                <BookCard
                  key={book.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <BookImage src={book.coverImage}>
                    {!book.coverImage && 'No Cover Image'}
                  </BookImage>
                  <BookTitle>{book.title}</BookTitle>
                  <div style={{ marginBottom: '1rem' }}>
                    <BookGenre>{book.genre || 'No Genre'}</BookGenre>
                    <BookStatus published={book.published}>
                      {book.published ? 'Published' : 'Draft'}
                    </BookStatus>
                  </div>
                  <p style={{ color: '#636e72', fontSize: '0.9rem', marginBottom: '1rem' }}>
                    {book.description.substring(0, 100)}...
                  </p>
                  <BookActions>
                    <ActionButton variant="edit" onClick={() => handleEditBook(book)}>
                      Edit
                    </ActionButton>
                    <ActionButton onClick={() => handleDeleteBook(book.id)}>
                      Delete
                    </ActionButton>
                  </BookActions>
                </BookCard>
              ))}
            </BooksGrid>
          </>
        )}

        {activeTab === 'testimonials' && (
          <>
            <AddBookButton
              onClick={() => {
                setEditingTestimonial(null);
                setTestimonialFormData({
                  reviewer: '',
                  quote: '',
                  rating: 5,
                  bookTitle: ''
                });
                setShowTestimonialModal(true);
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              + Add New Testimonial
            </AddBookButton>

            <BooksGrid>
              {testimonials.map(testimonial => (
                <BookCard
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div style={{ padding: '1rem' }}>
                    <h3 style={{ color: '#2d3436', marginBottom: '0.5rem' }}>{testimonial.reviewer}</h3>
                    <p style={{ color: '#636e72', fontStyle: 'italic', marginBottom: '1rem' }}>
                      "{testimonial.quote}"
                    </p>
                    <div style={{ marginBottom: '1rem' }}>
                      <span style={{ color: '#f39c12' }}>{'★'.repeat(testimonial.rating)}</span>
                      <span style={{ color: '#ddd' }}>{'★'.repeat(5 - testimonial.rating)}</span>
                    </div>
                    {testimonial.bookTitle && (
                      <p style={{ color: '#667eea', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Book: {testimonial.bookTitle}
                      </p>
                    )}
                    <BookActions>
                      <ActionButton variant="edit" onClick={() => {
                        setEditingTestimonial(testimonial);
                        setTestimonialFormData({
                          reviewer: testimonial.reviewer,
                          quote: testimonial.quote,
                          rating: testimonial.rating,
                          bookTitle: testimonial.bookTitle || ''
                        });
                        setShowTestimonialModal(true);
                      }}>
                        Edit
                      </ActionButton>
                      <ActionButton onClick={() => handleDeleteTestimonial(testimonial.id)}>
                        Delete
                      </ActionButton>
                    </BookActions>
                  </div>
                </BookCard>
              ))}
            </BooksGrid>
          </>
        )}

        {activeTab === 'gallery' && (
          <>
            <AddBookButton
              onClick={() => {
                setEditingGalleryImage(null);
                setGalleryFormData({
                  description: '',
                  bookId: '',
                  image: null
                });
                setShowGalleryModal(true);
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              + Add Gallery Image
            </AddBookButton>

            <BooksGrid>
              {galleryImages.map(image => (
                <BookCard
                  key={image.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <BookImage src={image.imagePath}>
                    {!image.imagePath && 'No Image'}
                  </BookImage>
                  <BookTitle>{image.description || 'No Description'}</BookTitle>
                  <div style={{ marginBottom: '1rem' }}>
                    <BookGenre>
                      {books.find(b => b.id.toString() === image.bookId.toString())?.title || 'No Book Linked'}
                    </BookGenre>
                    {image.bookId && (
                      <BookStatus published={true} style={{ marginLeft: '0.5rem', background: '#00b894' }}>
                        Published
                      </BookStatus>
                    )}
                  </div>
                  <p style={{ color: '#636e72', fontSize: '0.9rem', marginBottom: '1rem' }}>
                    {image.description ? image.description.substring(0, 100) + '...' : 'No description available'}
                  </p>
                  <BookActions>
                    <ActionButton variant="edit" onClick={() => {
                      setEditingGalleryImage(image);
                      setGalleryFormData({
                        description: image.description,
                        bookId: image.bookId,
                        image: null
                      });
                      setShowGalleryModal(true);
                    }}>
                      Edit
                    </ActionButton>
                    <ActionButton onClick={() => handleDeleteGalleryImage(image.id)}>
                      Delete
                    </ActionButton>
                  </BookActions>
                </BookCard>
              ))}
            </BooksGrid>
          </>
        )}

        {activeTab === 'settings' && (
          <div>
            <h2>Display Settings</h2>
            <p style={{ color: '#636e72', marginBottom: '2rem' }}>
              Control how many items are displayed on the front page
            </p>
            
            <Form onSubmit={handleDisplaySettingsSubmit}>
              <FormGroup>
                <Label>Maximum Featured Books to Display</Label>
                <Input
                  type="number"
                  name="maxFeaturedBooks"
                  value={displaySettings.maxFeaturedBooks}
                  onChange={handleDisplaySettingsChange}
                  min="1"
                  max="20"
                  required
                />
                <small style={{ color: '#636e72', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                  Number of books shown in the Featured Books section (1-20)
                </small>
              </FormGroup>

              <FormGroup>
                <Label>Maximum Gallery Images to Display</Label>
                <Input
                  type="number"
                  name="maxGalleryImages"
                  value={displaySettings.maxGalleryImages}
                  onChange={handleDisplaySettingsChange}
                  min="1"
                  max="50"
                  required
                />
                <small style={{ color: '#636e72', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                  Number of character images shown in the Character Gallery section (1-50)
                </small>
              </FormGroup>

              <ButtonGroup>
                <Button type="submit" variant="primary">
                  Save Display Settings
                </Button>
              </ButtonGroup>
            </Form>
          </div>
        )}

        {activeTab === 'portfolio' && (
          <>
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ color: '#2d3436', marginBottom: '1rem' }}>Complete Book Portfolio</h2>
              <p style={{ color: '#636e72', fontSize: '1.1rem', lineHeight: '1.6' }}>
                This is your complete book catalog showing all books (published and unpublished). 
                The front page only shows featured books based on your display settings, but this portfolio shows everything.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
              <div style={{ 
                background: 'linear-gradient(45deg, #00b894, #00a085)', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '25px', 
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                Total Books: {books.length}
              </div>
              <div style={{ 
                background: 'linear-gradient(45deg, #667eea, #764ba2)', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '25px', 
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                Published: {books.filter(b => b.published).length}
              </div>
              <div style={{ 
                background: 'linear-gradient(45deg, #fdcb6e, #e17055)', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '25px', 
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                Drafts: {books.filter(b => !b.published).length}
              </div>
            </div>

            <BooksGrid>
              {books.length > 0 ? books.map(book => (
                <BookCard
                  key={book.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <BookImage src={book.coverImage}>
                    {!book.coverImage && 'No Cover Image'}
                  </BookImage>
                  <BookTitle>{book.title}</BookTitle>
                  <div style={{ marginBottom: '1rem' }}>
                    <BookGenre>{book.genre || 'No Genre'}</BookGenre>
                    <BookStatus published={book.published}>
                      {book.published ? 'Published' : 'Draft'}
                    </BookStatus>
                  </div>
                  <p style={{ color: '#636e72', fontSize: '0.9rem', marginBottom: '1rem' }}>
                    {book.description.substring(0, 100)}...
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {book.publishedDate && (
                      <p style={{ color: '#667eea', fontSize: '0.8rem', fontWeight: '500', margin: 0 }}>
                        📅 {new Date(book.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </p>
                    )}
                    {book.pages && (
                      <p style={{ color: '#00b894', fontSize: '0.8rem', fontWeight: '500', margin: 0 }}>
                        📖 {book.pages} pages
                      </p>
                    )}
                    {book.rating && (
                      <p style={{ color: '#fdcb6e', fontSize: '0.8rem', fontWeight: '500', margin: 0 }}>
                        ⭐ {book.rating}
                      </p>
                    )}
                  </div>
                  <BookActions>
                    <ActionButton variant="edit" onClick={() => handleEditBook(book)}>
                      Edit
                    </ActionButton>
                    <ActionButton onClick={() => handleDeleteBook(book.id)}>
                      Delete
                    </ActionButton>
                  </BookActions>
                </BookCard>
              )) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#636e72' }}>
                  <h3 style={{ marginBottom: '1rem', color: '#2d3436' }}>No Books Yet</h3>
                  <p>Start building your portfolio by adding your first book!</p>
                </div>
              )}
            </BooksGrid>
          </>
        )}

        {activeTab === 'about' && (
          <div>
            <SectionTitle>About Page Management</SectionTitle>
            
            <FormGroup>
              <Label>Profile Image</Label>
              <FileInput
                type="file"
                accept="image/*"
                onChange={(e) => handleAboutImageUpload(e.target.files[0])}
              />
              {aboutData.profileImage && (
                <div style={{ marginTop: '1rem' }}>
                  <img 
                    src={`${API}${aboutData.profileImage}`} 
                    alt="Profile" 
                    style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '50%' }}
                  />
                </div>
              )}
            </FormGroup>
            
            <FormGroup>
              <Label>Bio/About Text</Label>
              <TextArea
                value={aboutData.bio}
                onChange={(e) => setAboutData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Enter bio text..."
                rows={8}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Inspirational Quote</Label>
              <TextArea
                value={aboutData.quote}
                onChange={(e) => setAboutData(prev => ({ ...prev, quote: e.target.value }))}
                placeholder="Enter inspirational quote..."
                rows={3}
              />
            </FormGroup>
            
            <SubmitButton
              onClick={handleUpdateAboutData}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Update About Page
            </SubmitButton>
          </div>
        )}

        {activeTab === 'bonus' && (
          <div>
            <SectionTitle>Bonus Content Management</SectionTitle>
            
            <AddBookButton
              onClick={() => {
                setEditingBonusContent(null);
                setBonusContentFormData({
                  title: '',
                  content: '',
                  excerpt: '',
                  category: 'Writing',
                  readTime: ''
                });
                setShowBonusContentModal(true);
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              + Add New Bonus Content
            </AddBookButton>
            
            <div style={{ marginTop: '3rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Existing Bonus Content</h3>
              {bonusContent.map(content => (
                <div key={content.id} style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '10px',
                  marginBottom: '1rem',
                  border: '1px solid #e1e5e9'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#2d3436' }}>{content.title}</h4>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                    <span style={{ 
                      background: '#667eea', 
                      color: 'white', 
                      padding: '0.2rem 0.5rem', 
                      borderRadius: '4px', 
                      fontSize: '0.8rem' 
                    }}>
                      {content.category}
                    </span>
                    {content.readTime && (
                      <span style={{ color: '#636e72', fontSize: '0.9rem' }}>
                        {content.readTime} min read
                      </span>
                    )}
                    <span style={{ color: '#636e72', fontSize: '0.9rem' }}>
                      {new Date(content.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p style={{ color: '#636e72', margin: '0.5rem 0' }}>
                    {content.excerpt || content.content.substring(0, 150) + '...'}
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleEditBonusContent(content)}
                      style={{
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBonusContent(content.id)}
                      style={{
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'subscribers' && (
          <div>
            <SectionTitle>Newsletter Subscribers</SectionTitle>
            
            <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#2d3436' }}>Subscriber Statistics</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#667eea' }}>{subscribers.length}</div>
                  <div style={{ color: '#636e72' }}>Total Subscribers</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00b894' }}>
                    {subscribers.filter(s => s.verified).length}
                  </div>
                  <div style={{ color: '#636e72' }}>Verified</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fdcb6e' }}>
                    {subscribers.filter(s => !s.verified).length}
                  </div>
                  <div style={{ color: '#636e72' }}>Pending</div>
                </div>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <thead>
                  <tr style={{ background: '#667eea', color: 'white' }}>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '1rem', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '1rem', textAlign: 'center' }}>Subscribed Date</th>
                    <th style={{ padding: '1rem', textAlign: 'center' }}>Verified Date</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length > 0 ? subscribers.map((subscriber, index) => (
                    <tr key={subscriber.id} style={{ borderBottom: '1px solid #e1e5e9' }}>
                      <td style={{ padding: '1rem', fontWeight: '500' }}>{subscriber.email}</td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.3rem 0.8rem',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          background: subscriber.verified ? '#00b894' : '#fdcb6e',
                          color: 'white'
                        }}>
                          {subscriber.verified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center', color: '#636e72' }}>
                        {new Date(subscriber.subscribedAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center', color: '#636e72' }}>
                        {subscriber.verified ? new Date(subscriber.subscribedAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: '#636e72' }}>
                        No subscribers yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <SectionTitle>Website Analytics & Statistics</SectionTitle>
              <Button 
                onClick={clearAnalytics}
                style={{ background: '#e74c3c', fontSize: '0.9rem', padding: '0.5rem 1rem' }}
              >
                Clear Analytics Data
              </Button>
            </div>
            
            {/* Overview Cards */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1.5rem', 
              marginBottom: '3rem' 
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '2rem',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {analyticsData.totalVisits.toLocaleString()}
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.9 }}>Total Visits</div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, #00b894 0%, #00cec9 100%)',
                color: 'white',
                padding: '2rem',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 25px rgba(0, 184, 148, 0.3)'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {analyticsData.todayVisits.toLocaleString()}
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.9 }}>Today's Visits</div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)',
                color: 'white',
                padding: '2rem',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 25px rgba(253, 203, 110, 0.3)'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {analyticsData.weekVisits.toLocaleString()}
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.9 }}>This Week</div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)',
                color: 'white',
                padding: '2rem',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 10px 25px rgba(162, 155, 254, 0.3)'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {analyticsData.uniqueVisitors.toLocaleString()}
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.9 }}>Unique Visitors</div>
              </div>
            </div>

            {/* Content Statistics */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '2rem', 
              marginBottom: '3rem' 
            }}>
              {/* Page Views */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '15px',
                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
              }}>
                <h3 style={{ color: '#2d3436', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Page Views</h3>
                {Object.entries(analyticsData.pageViews).map(([page, views]) => (
                  <div key={page} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #f1f2f6'
                  }}>
                    <span style={{ textTransform: 'capitalize', fontWeight: '500' }}>{page}</span>
                    <span style={{ 
                      background: '#667eea', 
                      color: 'white', 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '15px',
                      fontSize: '0.9rem'
                    }}>
                      {views.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              {/* Device Types */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '15px',
                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
              }}>
                <h3 style={{ color: '#2d3436', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Device Types</h3>
                {Object.entries(analyticsData.devices).map(([device, count]) => (
                  <div key={device} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #f1f2f6'
                  }}>
                    <span style={{ fontWeight: '500' }}>{device}</span>
                    <span style={{ 
                      background: '#00b894', 
                      color: 'white', 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '15px',
                      fontSize: '0.9rem'
                    }}>
                      {count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              {/* Browser Statistics */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '15px',
                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
              }}>
                <h3 style={{ color: '#2d3436', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Browsers</h3>
                {Object.entries(analyticsData.browsers).map(([browser, count]) => (
                  <div key={browser} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #f1f2f6'
                  }}>
                    <span style={{ textTransform: 'capitalize', fontWeight: '500' }}>{browser}</span>
                    <span style={{ 
                      background: '#fdcb6e', 
                      color: 'white', 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '15px',
                      fontSize: '0.9rem'
                    }}>
                      {count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Daily Visits Chart */}
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '15px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
              marginBottom: '3rem'
            }}>
              <h3 style={{ color: '#2d3436', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Daily Visits (Last 30 Days)</h3>
              <div style={{ 
                display: 'flex', 
                alignItems: 'end', 
                gap: '4px', 
                height: '200px',
                padding: '1rem 0'
              }}>
                {analyticsData.dailyVisits.map((day, index) => {
                  const maxVisits = Math.max(...analyticsData.dailyVisits.map(d => d.count));
                  const height = maxVisits > 0 ? (day.count / maxVisits) * 160 : 0;
                  return (
                    <div key={index} style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        width: '100%',
                        height: `${height}px`,
                        borderRadius: '2px',
                        minHeight: day.count > 0 ? '2px' : '0',
                        position: 'relative'
                      }}>
                        {day.count > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '-25px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '0.7rem',
                            color: '#636e72',
                            whiteSpace: 'nowrap'
                          }}>
                            {day.count}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.6rem',
                        color: '#b2bec3',
                        transform: 'rotate(-45deg)',
                        whiteSpace: 'nowrap'
                      }}>
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Referrers and Recent Visits */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
              gap: '2rem', 
              marginBottom: '3rem' 
            }}>
              {/* Top Referrers */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '15px',
                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
              }}>
                <h3 style={{ color: '#2d3436', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Top Referrers</h3>
                {analyticsData.topReferrers.slice(0, 8).map((referrer, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #f1f2f6'
                  }}>
                    <span style={{ fontWeight: '500', fontSize: '0.9rem' }}>
                      {referrer.referrer.length > 25 ? referrer.referrer.substring(0, 25) + '...' : referrer.referrer}
                    </span>
                    <span style={{ 
                      background: '#a29bfe', 
                      color: 'white', 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '15px',
                      fontSize: '0.8rem'
                    }}>
                      {referrer.count}
                    </span>
                  </div>
                ))}
              </div>

              {/* Recent Visits */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '15px',
                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
              }}>
                <h3 style={{ color: '#2d3436', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Recent Visits</h3>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {analyticsData.recentVisits.slice(0, 10).map((visit, index) => (
                    <div key={index} style={{ 
                      padding: '0.75rem 0',
                      borderBottom: '1px solid #f1f2f6',
                      fontSize: '0.85rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>{visit.pageType}</span>
                        <span style={{ color: '#636e72' }}>
                          {new Date(visit.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ color: '#636e72', fontSize: '0.8rem' }}>
                        {visit.device} • {visit.browser} • {visit.ip.substring(0, 10)}...
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Content Statistics */}
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '15px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
            }}>
              <h3 style={{ color: '#2d3436', marginBottom: '1.5rem', fontSize: '1.3rem' }}>Content Statistics</h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1.5rem' 
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#667eea' }}>{books.length}</div>
                  <div style={{ color: '#636e72' }}>Total Books</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00b894' }}>
                    {books.filter(b => b.published).length}
                  </div>
                  <div style={{ color: '#636e72' }}>Published Books</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fdcb6e' }}>
                    {testimonials.length}
                  </div>
                  <div style={{ color: '#636e72' }}>Testimonials</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#a29bfe' }}>
                    {subscribers.length}
                  </div>
                  <div style={{ color: '#636e72' }}>Subscribers</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e17055' }}>
                    {galleryImages.length}
                  </div>
                  <div style={{ color: '#636e72' }}>Gallery Images</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </ContentArea>

      <AnimatePresence>
        {showBonusContentModal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowBonusContentModal(false);
              setEditingBonusContent(null);
            }}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>{editingBonusContent ? 'Edit Bonus Content' : 'Add New Bonus Content'}</ModalTitle>
              <Form onSubmit={editingBonusContent ? handleUpdateBonusContent : handleAddBonusContent}>
                <FormGroup>
                  <Label>Title *</Label>
                  <Input
                    type="text"
                    value={bonusContentFormData.title}
                    onChange={(e) => setBonusContentFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Content title..."
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Category</Label>
                  <Select
                    value={bonusContentFormData.category}
                    onChange={(e) => setBonusContentFormData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="Writing">Writing</option>
                    <option value="Behind the Scenes">Behind the Scenes</option>
                    <option value="Character Notes">Character Notes</option>
                    <option value="World Building">World Building</option>
                    <option value="Deleted Scenes">Deleted Scenes</option>
                    <option value="Author Notes">Author Notes</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Excerpt (Preview Text)</Label>
                  <TextArea
                    value={bonusContentFormData.excerpt}
                    onChange={(e) => setBonusContentFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                    placeholder="Brief excerpt or description..."
                    rows={3}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Read Time (minutes)</Label>
                  <Input
                    type="number"
                    value={bonusContentFormData.readTime}
                    onChange={(e) => setBonusContentFormData(prev => ({ ...prev, readTime: e.target.value }))}
                    placeholder="Estimated read time..."
                    min="1"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Content (HTML supported) *</Label>
                  <TextArea
                    value={bonusContentFormData.content}
                    onChange={(e) => setBonusContentFormData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Full content with HTML formatting..."
                    rows={12}
                    style={{ fontFamily: 'monospace' }}
                    required
                  />
                  <small style={{ color: '#636e72', marginTop: '0.5rem', display: 'block' }}>
                    HTML tags supported: &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;blockquote&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;br&gt;
                  </small>
                </FormGroup>
                
                <ButtonGroup>
                  <Button 
                    type="button" 
                    onClick={() => {
                      setShowBonusContentModal(false);
                      setEditingBonusContent(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingBonusContent ? 'Update Bonus Content' : 'Add Bonus Content'}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}

        {showModal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>{editingBook ? 'Edit Book' : 'Add New Book'}</h2>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Title *</Label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Description *</Label>
                  <TextArea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Genre</Label>
                  <Select
                    name="genre"
                    value={formData.genre}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Genre</option>
                    <option value="Literary Fiction">Literary Fiction</option>
                    <option value="Fantasy">Fantasy</option>
                    <option value="Short Stories">Short Stories</option>
                    <option value="Romance">Romance</option>
                    <option value="Mystery">Mystery</option>
                    <option value="Thriller">Thriller</option>
                    <option value="Science Fiction">Science Fiction</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Full Blurb</Label>
                  <TextArea
                    name="blurb"
                    value={formData.blurb}
                    onChange={handleInputChange}
                    placeholder="Detailed book description for the book page"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Published Date</Label>
                  <Input
                    type="date"
                    name="publishedDate"
                    value={formData.publishedDate}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Pages (Approximate)</Label>
                  <Input
                    type="number"
                    name="pages"
                    value={formData.pages}
                    onChange={handleInputChange}
                    placeholder="e.g., 350"
                    min="1"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Rating (1-5 stars)</Label>
                  <Input
                    type="number"
                    name="rating"
                    value={formData.rating}
                    onChange={handleInputChange}
                    placeholder="e.g., 4.8"
                    min="1"
                    max="5"
                    step="0.1"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Amazon Link</Label>
                  <Input
                    type="url"
                    name="amazonLink"
                    value={formData.amazonLink}
                    onChange={handleInputChange}
                    placeholder="https://amazon.com/..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Goodreads Link</Label>
                  <Input
                    type="url"
                    name="goodreadsLink"
                    value={formData.goodreadsLink}
                    onChange={handleInputChange}
                    placeholder="https://goodreads.com/..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Cover Image</Label>
                  <Input
                    type="file"
                    name="coverImage"
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Gallery Images (Character/Promo Images)</Label>
                  <Input
                    type="file"
                    name="galleryImages"
                    onChange={handleInputChange}
                    accept="image/*"
                    multiple
                  />
                  <small style={{ color: '#636e72', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                    Select multiple images for the gallery showcase (up to 10 images)
                  </small>
                </FormGroup>

                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    name="published"
                    checked={formData.published}
                    onChange={handleInputChange}
                  />
                  <Label>Published (visible on website)</Label>
                </CheckboxContainer>

                <ButtonGroup>
                  <Button type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingBook ? 'Update Book' : 'Add Book'}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}

        {showTestimonialModal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTestimonialModal(false)}
          >
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h2>{editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}</h2>
              <Form onSubmit={handleTestimonialSubmit}>
                <FormGroup>
                  <Label>Reviewer Name</Label>
                  <Input
                    type="text"
                    name="reviewer"
                    value={testimonialFormData.reviewer}
                    onChange={handleTestimonialInputChange}
                    required
                    placeholder="John Doe"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Quote/Review</Label>
                  <TextArea
                    name="quote"
                    value={testimonialFormData.quote}
                    onChange={handleTestimonialInputChange}
                    required
                    placeholder="This book was absolutely amazing..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Rating</Label>
                  <Select
                    name="rating"
                    value={testimonialFormData.rating}
                    onChange={handleTestimonialInputChange}
                  >
                    <option value={5}>5 Stars</option>
                    <option value={4}>4 Stars</option>
                    <option value={3}>3 Stars</option>
                    <option value={2}>2 Stars</option>
                    <option value={1}>1 Star</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Book Title (Optional)</Label>
                  <Input
                    type="text"
                    name="bookTitle"
                    value={testimonialFormData.bookTitle}
                    onChange={handleTestimonialInputChange}
                    placeholder="Which book is this review for?"
                  />
                </FormGroup>

                <ButtonGroup>
                  <Button type="button" onClick={() => setShowTestimonialModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingTestimonial ? 'Update Testimonial' : 'Add Testimonial'}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}

        {showGalleryModal && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowGalleryModal(false)}
          >
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h2>{editingGalleryImage ? 'Edit Gallery Image' : 'Add New Gallery Image'}</h2>
              <Form onSubmit={handleGallerySubmit}>
                <FormGroup>
                  <Label>Image Description</Label>
                  <TextArea
                    name="description"
                    value={galleryFormData.description}
                    onChange={handleGalleryFormChange}
                    required
                    placeholder="Describe this character or scene (e.g., 'This is Lyra, the main protagonist...')"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Link to Book</Label>
                  <Select
                    name="bookId"
                    value={galleryFormData.bookId}
                    onChange={handleGalleryFormChange}
                    required
                  >
                    <option value="">Select a book</option>
                    {books.map(book => (
                      <option key={book.id} value={book.id}>
                        {book.title}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Gallery Image</Label>
                  <Input
                    type="file"
                    name="image"
                    onChange={handleGalleryFormChange}
                    accept="image/*"
                    required={!editingGalleryImage}
                  />
                  <small style={{ color: '#636e72', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                    Upload character images, promotional art, or scene illustrations
                  </small>
                </FormGroup>

                <ButtonGroup>
                  <Button type="button" onClick={() => setShowGalleryModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingGalleryImage ? 'Update Gallery Image' : 'Add Gallery Image'}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </DashboardContainer>
  );
};

export default AdminDashboard;
